import { Ico } from "./Icons";
import FolderTreeNode from "./FolderTreeNode";
import TreeSection, { type TreeContextAction } from "./TreeSection";
import { useSnapshot } from "valtio";
import { contentCatalogStore } from "../contentCatalogStore";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
    createCatalogDropId,
    type CatalogDropTarget,
} from "../contentCatalogDnd";
import { ContentCatalogFolder } from "@models/contentCatalogFolder.model";
import { Ad } from "@models/Ad.model";
import type { AdMenuItemsFn, FolderMenuItemsFn } from "../contentCatalog";
import { appStore } from "@store/appStore";

interface FolderTreeProps {
    adMenuItems?: AdMenuItemsFn;
    folderMenuItems?: FolderMenuItemsFn;
    collectionMenuItems?: FolderMenuItemsFn;
    collectionAdMenuItems?: AdMenuItemsFn;
    onAdClick?: (ad: Ad) => void;
    onCreateRootFolder?: () => void;
    onCreateRootCollection?: () => void;
    enableDnd?: boolean;
}

export default function FolderTree({
    adMenuItems,
    folderMenuItems,
    collectionMenuItems,
    collectionAdMenuItems,
    onAdClick,
    onCreateRootFolder,
    onCreateRootCollection,
    enableDnd,
}: FolderTreeProps) {
    const snapStore = useSnapshot(contentCatalogStore);
    const hasAccess = (feature: string) =>
        appStore.loggedInUser?.permissions?.hasAccess?.(feature) ?? false;
    const treeRef = useRef<HTMLDivElement | null>(null);
    const allContentQuery = (snapStore.query || "").trim();
    const isSearchingAllContent = allContentQuery.length > 0;
    const folderSearch = snapStore.folderSearch;

    const visibleAllContentRoots = useMemo(() => {
        if (!isSearchingAllContent) return contentCatalogStore.folders as ContentCatalogFolder[];
        return (contentCatalogStore.folders as ContentCatalogFolder[]).filter((node) =>
            folderSearch.visibleFolderIds.has(node.id),
        );
    }, [folderSearch.visibleFolderIds, isSearchingAllContent, snapStore.folders]);

    const handleSelect = useCallback((id: string) => {
        contentCatalogStore.selectedId = id;
        if (contentCatalogStore.panelMode === "filter") contentCatalogStore.panelMode = "browse";
    }, [contentCatalogStore.panelMode]);

    useEffect(() => {
        if (!snapStore.pendingScrollFolderId) return;

        const targetId = snapStore.pendingScrollFolderId;
        const rafId = requestAnimationFrame(() => {
            const targetEl = treeRef.current?.querySelector(`[data-folder-node-id="${targetId}"]`) as HTMLElement | null;
            if (targetEl) {
                targetEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
                contentCatalogStore.pendingScrollFolderId = null;
            }
        });

        return () => cancelAnimationFrame(rafId);
    }, [snapStore.pendingScrollFolderId, snapStore.folders, snapStore.collections]);

    const allRootDropData: CatalogDropTarget = {
        kind: "folder-container",
        section: "all",
        parentFolderId: null,
    };

    const {
        setNodeRef: setAllRootDropRef,
        isOver: isAllRootDropOver,
    } = useDroppable({
        id: createCatalogDropId("tree", allRootDropData, "all-root-folders"),
        data: allRootDropData,
        disabled: !enableDnd,
    });

    const collectionRootActions: TreeContextAction[] = useMemo(() => {
        if (!onCreateRootCollection || !hasAccess("Collections")) return [];
        return [{ label: "New Collection", onSelect: onCreateRootCollection }];
    }, [onCreateRootCollection]);

    const allContentRootActions: TreeContextAction[] = useMemo(() => {
        if (!onCreateRootFolder || !hasAccess("Ad Library")) return [];
        return [{ label: "New Folder", onSelect: onCreateRootFolder }];
    }, [onCreateRootFolder]);

    const hasCollections = (contentCatalogStore.collections as ContentCatalogFolder[]).length > 0;

    return (
        <div className="folder-tree" ref={treeRef}>
            {hasCollections && hasAccess("Collections") && (
                <TreeSection
                    label="Collections"
                    nodeId="__collections__"
                    icon={<Ico.CollectionFilled />}
                    selectedId={snapStore.selectedId}
                    onSelect={handleSelect}
                    contextActions={collectionRootActions}
                >
                    <div className="tree-dropzone">
                        {contentCatalogStore.collections.map((node) => (
                            <FolderTreeNode
                                key={node.id}
                                node={node}
                                depth={0}
                                parentFolderId={null}
                                section="collection"
                                selectedId={snapStore.selectedId}
                                onSelect={handleSelect}
                                iconType="collection"
                                onAdClick={onAdClick}
                                adMenuItems={collectionAdMenuItems}
                                folderMenuItems={collectionMenuItems}
                                enableDnd={enableDnd}
                            />
                        ))}
                    </div>
                </TreeSection>
            )}

            {hasAccess("Ad Library") && <TreeSection
                label="All Content"
                nodeId="__all__"
                icon={<Ico.AllContent />}
                selectedId={snapStore.selectedId}
                onSelect={handleSelect}
                forceOpen={isSearchingAllContent}
                contextActions={allContentRootActions}
            >
                <div
                    ref={setAllRootDropRef}
                    className={`tree-dropzone cc-dropzone${isAllRootDropOver ? " cc-dropzone--over" : ""}`}
                >
                    {visibleAllContentRoots.map((node) => (
                        <FolderTreeNode
                            key={node.id}
                            node={node}
                            depth={0}
                            parentFolderId={null}
                            section="all"
                            selectedId={snapStore.selectedId}
                            onSelect={handleSelect}
                            iconType="folder"
                            searchQuery={isSearchingAllContent ? allContentQuery : undefined}
                            visibleFolderIds={isSearchingAllContent ? folderSearch.visibleFolderIds : undefined}
                            autoExpandFolderIds={isSearchingAllContent ? folderSearch.autoExpandFolderIds : undefined}
                            visibleAdIdsByFolder={isSearchingAllContent ? folderSearch.visibleAdIdsByFolder : undefined}
                            onAdClick={onAdClick}
                            adMenuItems={adMenuItems}
                            folderMenuItems={folderMenuItems}
                            enableDnd={enableDnd}
                        />
                    ))}
                    {isSearchingAllContent && visibleAllContentRoots.length === 0 && (
                        <div className="tree-empty-state">No matching folders or ads.</div>
                    )}
                </div>
            </TreeSection>}
        </div>
    );
}
